import React from 'react';
import { MOBILITY_RUN_TILE_STATUSES } from '@hopdrive/feature-order-index';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { applyRunEdit } from '../services/runEditService';
import type { StopEditInput } from '../services/runEditService';

/**
 * Draft state for run edit mode (docs/run-edit-mode-design.md).
 *
 * Nothing is mutated live: entering edit mode snapshots the current stop
 * order into a draft; reorders and removals stage against that draft until
 * the coordinator reviews and applies them in one transaction (or discards).
 *
 * Locking (v1): completed/skipped stops, the active stop
 * (in_transit_to / arrived / in_service), and the dealership anchors
 * (first/last stop) can neither move nor be removed.
 */

export const LOCKED_STOP_STATUSES = new Set([
  'in_transit_to',
  'arrived',
  'in_service',
  'service_in_progress',
  'completed',
  'skipped',
]);

/** Completed and canceled runs, both spellings, from the run vocabulary. */
export const UNEDITABLE_RUN_STATUSES = new Set<string>([...MOBILITY_RUN_TILE_STATUSES.completed, ...MOBILITY_RUN_TILE_STATUSES.cancelled]);

/**
 * The editable field set for a stop (phase 2 — inline stop editing).
 * Address edits travel with the resolved coordinates/location so the map and
 * downstream geofencing stay correct; they're staged together by the form's
 * address autocomplete, never as free text.
 */
export interface StopFieldValues {
  customer_name: string;
  customer_phone: string;
  service_description: string;
  address: string;
  address_lat: number | null;
  address_lng: number | null;
  location_id: number | null;
  vehicle_year: number | null;
  vehicle_make: string;
  vehicle_model: string;
}

export const STOP_FIELD_LABELS: Record<keyof StopFieldValues, string> = {
  customer_name: 'Contact name',
  customer_phone: 'Phone',
  service_description: 'Service description',
  address: 'Address',
  address_lat: 'Address',
  address_lng: 'Address',
  location_id: 'Address',
  vehicle_year: 'Vehicle year',
  vehicle_make: 'Vehicle make',
  vehicle_model: 'Vehicle model',
};

interface EditableStop {
  id: number;
  status: string;
  serviceOrderId: number | null;
  fields: StopFieldValues;
}

interface UseRunEditModeArgs {
  runId: number | null;
  /** Live stops in server order (the page's subscription keeps these fresh). */
  stops: EditableStop[];
  /** Called after a successful apply (the subscription delivers the new data). */
  onApplied?: (summary: { removed: number; reordered: boolean; edited: number; added: number }) => void;
}

const DETAIL_FIELDS = ['customer_name', 'customer_phone', 'service_description'] as const;
const STOP_FIELDS = ['address', 'address_lat', 'address_lng', 'location_id'] as const;
const VEHICLE_FIELDS = ['vehicle_year', 'vehicle_make', 'vehicle_model'] as const;

/** Compare a staged value to its baseline (treat null/undefined/'' as equal for strings). */
const fieldChanged = (a: unknown, b: unknown) => (a ?? '') !== (b ?? '');

export function useRunEditMode({ runId, stops, onApplied }: UseRunEditModeArgs) {
  const [editing, setEditing] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [applyError, setApplyError] = React.useState<string | null>(null);
  // Draft = remaining stop ids in draft order; removed = staged removals.
  const [draftOrder, setDraftOrder] = React.useState<number[]>([]);
  const [removedIds, setRemovedIds] = React.useState<number[]>([]);
  // Staged field edits per stop (sparse — only touched fields).
  const [fieldEdits, setFieldEdits] = React.useState<Record<number, Partial<StopFieldValues>>>({});
  // Staged additions (phase 3): negative temp id → source appointment id and
  // the customer-facing service window (formatted appointment time) that the
  // server persists onto the new stop. Temp ids live in draftOrder alongside
  // real stop ids until apply.
  const [addedStops, setAddedStops] = React.useState<Record<number, { appointmentId: number; serviceWindow: string | null }>>({});
  const nextTempIdRef = React.useRef(-1);
  // Snapshot of the server order at enterEdit — the diff baseline.
  const baselineRef = React.useRef<number[]>([]);
  // Snapshot of the field values at enterEdit — the field-diff baseline.
  const baselineFieldsRef = React.useRef<Map<number, StopFieldValues>>(new Map());
  const serviceOrderIdRef = React.useRef<Map<number, number | null>>(new Map());

  const statusById = React.useMemo(
    () => new Map(stops.map(s => [s.id, s.status])),
    [stops]
  );

  // Anchors are the first/last stops of the baseline (dealership depart/return).
  const anchorStartId = baselineRef.current[0] ?? null;
  const anchorEndId = baselineRef.current[baselineRef.current.length - 1] ?? null;

  const isAnchorStop = React.useCallback(
    (id: number) => id === anchorStartId || id === anchorEndId,
    [anchorStartId, anchorEndId]
  );

  const isLockedStop = React.useCallback(
    (id: number) => LOCKED_STOP_STATUSES.has(statusById.get(id) ?? ''),
    [statusById]
  );

  const isMovable = React.useCallback(
    (id: number) => !isAnchorStop(id) && !isLockedStop(id),
    [isAnchorStop, isLockedStop]
  );

  const enterEdit = React.useCallback(() => {
    const order = stops.map(s => s.id);
    baselineRef.current = order;
    baselineFieldsRef.current = new Map(stops.map(s => [s.id, { ...s.fields }]));
    serviceOrderIdRef.current = new Map(stops.map(s => [s.id, s.serviceOrderId]));
    setDraftOrder(order);
    setRemovedIds([]);
    setFieldEdits({});
    setAddedStops({});
    setApplyError(null);
    setEditing(true);
  }, [stops]);

  const discard = React.useCallback(() => {
    setEditing(false);
    setDraftOrder([]);
    setRemovedIds([]);
    setFieldEdits({});
    setAddedStops({});
    setApplyError(null);
  }, []);

  // ── Added stops (phase 3) ── //

  const isAddedStop = React.useCallback((id: number) => id < 0, []);

  /** Stage an appointment as a new stop, inserted before the return anchor. */
  const addStopFromAppointment = React.useCallback((appointmentId: number, serviceWindow: string | null = null): number => {
    const tempId = nextTempIdRef.current--;
    setAddedStops(prev => ({ ...prev, [tempId]: { appointmentId, serviceWindow } }));
    setDraftOrder(prev => {
      const next = [...prev];
      const anchorIdx = anchorEndId != null ? next.indexOf(anchorEndId) : -1;
      next.splice(anchorIdx >= 0 ? anchorIdx : next.length, 0, tempId);
      return next;
    });
    return tempId;
  }, [anchorEndId]);

  // ── Field edits (phase 2) ── //

  const setStopFields = React.useCallback((id: number, patch: Partial<StopFieldValues>) => {
    setFieldEdits(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  /** Drop a stop's staged field edits, restoring its baseline values. */
  const revertStopFields = React.useCallback((id: number) => {
    setFieldEdits(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /** Baseline merged with staged edits — what the card/form should display. */
  const stagedFieldsFor = React.useCallback(
    (id: number): StopFieldValues | null => {
      const baseline = baselineFieldsRef.current.get(id);
      if (!baseline) return null;
      return { ...baseline, ...fieldEdits[id] };
    },
    [fieldEdits]
  );

  /** Fields whose staged value differs from the baseline. */
  const changedFieldsFor = React.useCallback(
    (id: number): Partial<StopFieldValues> => {
      const baseline = baselineFieldsRef.current.get(id);
      const edits = fieldEdits[id];
      if (!baseline || !edits) return {};
      const changed: Partial<StopFieldValues> = {};
      (Object.keys(edits) as (keyof StopFieldValues)[]).forEach(k => {
        if (fieldChanged(edits[k], baseline[k])) {
          (changed as Record<string, unknown>)[k] = edits[k];
        }
      });
      return changed;
    },
    [fieldEdits]
  );

  const editedStopIds = React.useMemo(
    () =>
      Object.keys(fieldEdits)
        .map(Number)
        .filter(id => !removedIds.includes(id))
        .filter(id => Object.keys(changedFieldsFor(id)).length > 0),
    [fieldEdits, removedIds, changedFieldsFor]
  );

  const moveStop = React.useCallback((id: number, dir: 'up' | 'down') => {
    setDraftOrder(prev => {
      const i = prev.indexOf(id);
      const j = dir === 'up' ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      if (!isMovable(prev[j])) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, [isMovable]);

  const moveStopTo = React.useCallback((dragId: number, targetId: number) => {
    if (dragId === targetId) return;
    if (!isMovable(dragId) || !isMovable(targetId)) return;
    setDraftOrder(prev => {
      const from = prev.indexOf(dragId);
      const to = prev.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
  }, [isMovable]);

  const removeStop = React.useCallback((id: number) => {
    if (!isMovable(id)) return;
    setDraftOrder(prev => prev.filter(x => x !== id));
    if (isAddedStop(id)) {
      // Removing a staged addition just unstages it — no server-side removal.
      setAddedStops(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setRemovedIds(prev => [...prev, id]);
    }
  }, [isMovable, isAddedStop]);

  const restoreStop = React.useCallback((id: number) => {
    setRemovedIds(prev => prev.filter(x => x !== id));
    setDraftOrder(prev => {
      // Re-insert before the return anchor (or at the end when there is none).
      const next = [...prev];
      const anchorIdx = anchorEndId != null ? next.indexOf(anchorEndId) : -1;
      next.splice(anchorIdx >= 0 ? anchorIdx : next.length, 0, id);
      return next;
    });
  }, [anchorEndId]);

  // ── Diff vs the baseline ── //
  const orderChanged = React.useMemo(() => {
    const baselineMinusRemoved = baselineRef.current.filter(id => !removedIds.includes(id));
    // Additions don't count as a reorder — compare existing stops only.
    const draftExisting = draftOrder.filter(id => id > 0);
    return baselineMinusRemoved.join(',') !== draftExisting.join(',');
    // baselineRef is stable for the lifetime of an edit session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftOrder, removedIds]);

  const addedCount = Object.keys(addedStops).length;
  const dirtyCount = removedIds.length + (orderChanged ? 1 : 0) + editedStopIds.length + addedCount;

  // Staged changes, or an Apply on its way, hold an app update. The guard
  // lives here because the run detail page is the only user of this hook.
  useReloadGuard((editing && dirtyCount > 0) || applying, 'Run edits');

  const apply = React.useCallback(async () => {
    if (!runId || dirtyCount === 0) return false;
    setApplying(true);
    setApplyError(null);

    // Group each stop's changed fields into the tables they live in.
    const stopEdits: StopEditInput[] = editedStopIds.map(id => {
      const changed = changedFieldsFor(id);
      const pick = (keys: readonly (keyof StopFieldValues)[]) => {
        const out: Record<string, unknown> = {};
        keys.forEach(k => {
          if (k in changed) out[k] = changed[k];
        });
        return Object.keys(out).length > 0 ? out : undefined;
      };
      return {
        stopId: id,
        serviceOrderId: serviceOrderIdRef.current.get(id) ?? null,
        detail: pick(DETAIL_FIELDS),
        stop: pick(STOP_FIELDS),
        vehicle: pick(VEHICLE_FIELDS),
      };
    });

    // Added stops travel as (appointmentId, position, serviceWindow) where
    // position is the final index in the combined order; the server splices
    // them in and persists the window onto the new stop's details.
    const addedStopInputs = draftOrder
      .map((id, idx) =>
        id < 0
          ? { appointmentId: addedStops[id].appointmentId, position: idx, serviceWindow: addedStops[id].serviceWindow }
          : null
      )
      .filter((a): a is { appointmentId: number; position: number; serviceWindow: string | null } => a !== null);

    const result = await applyRunEdit({
      runId,
      orderedStopIds: draftOrder.filter(id => id > 0),
      removedStopIds: removedIds,
      stopEdits,
      addedStops: addedStopInputs,
    });
    setApplying(false);
    if (!result.success) {
      setApplyError(result.error || 'Failed to apply changes');
      return false;
    }
    const summary = {
      removed: removedIds.length,
      reordered: orderChanged,
      edited: editedStopIds.length,
      added: addedStopInputs.length,
    };
    setEditing(false);
    setDraftOrder([]);
    setRemovedIds([]);
    setFieldEdits({});
    setAddedStops({});
    onApplied?.(summary);
    return true;
  }, [runId, dirtyCount, draftOrder, removedIds, addedStops, orderChanged, editedStopIds, changedFieldsFor, onApplied]);

  return {
    editing,
    enterEdit,
    discard,
    draftOrder,
    removedIds,
    moveStop,
    moveStopTo,
    removeStop,
    restoreStop,
    isAnchorStop,
    isLockedStop,
    isMovable,
    isAddedStop,
    addStopFromAppointment,
    addedStops,
    setStopFields,
    revertStopFields,
    stagedFieldsFor,
    changedFieldsFor,
    editedStopIds,
    orderChanged,
    dirtyCount,
    applying,
    applyError,
    apply,
  };
}
